import React from "react";
import { Link } from "react-router-dom";
import { Download, ArrowUpRight } from "lucide-react";
import { TYPE_META, fileUrl, audioSource } from "./resourceUtils";
import { ResourceThumb } from "./ResourceThumb";

const AudioPlayer = ({ r }) => {
    const src = r.file_id ? { kind: "audio", src: fileUrl(r.file_id) } : audioSource(r.url);
    if (!src) return null;
    if (src.kind === "iframe") {
        return (
            <iframe
                title={`${r.title} audio`}
                src={src.src}
                className="w-full rounded-xl"
                height="120"
                allow="autoplay"
                loading="lazy"
                data-testid={`resource-audio-embed-${r.id}`}
            />
        );
    }
    return (
        <audio controls preload="none" src={src.src} className="w-full" data-testid={`resource-audio-${r.id}`}>
            Your browser does not support audio playback.
        </audio>
    );
};

export const CardAction = ({ r, onWatch }) => {
    const meta = TYPE_META[r.type] || TYPE_META.link;
    if (r.type === "pdf" && r.file_id) {
        return (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                <a href={fileUrl(r.file_id)} target="_blank" rel="noopener noreferrer" className="res-btn" data-testid={`resource-action-${r.id}`}>
                    {meta.action} <ArrowUpRight size={16} strokeWidth={1.6} />
                </a>
                <a href={fileUrl(r.file_id, { download: true })} className="res-link inline-flex items-center gap-1.5" data-testid={`resource-save-${r.id}`}>
                    <Download size={15} strokeWidth={1.6} /> Save a copy
                </a>
            </div>
        );
    }
    if (r.type === "video") {
        return (
            <button type="button" onClick={() => onWatch(r)} className="res-btn" data-testid={`resource-action-${r.id}`}>
                {meta.action} <meta.Icon size={16} strokeWidth={1.6} />
            </button>
        );
    }
    if (r.type === "audio") {
        const playable = r.file_id || audioSource(r.url);
        if (playable) return <AudioPlayer r={r} />;
    }
    return (
        <a href={r.url} target="_blank" rel="noopener noreferrer" className="res-btn" data-testid={`resource-action-${r.id}`}>
            {meta.action} <ArrowUpRight size={16} strokeWidth={1.6} />
        </a>
    );
};

export const ResourceLibraryCard = ({ r, categoryName, onWatch, featured = false }) => {
    const meta = TYPE_META[r.type] || TYPE_META.link;
    return (
        <article className="res-card overflow-hidden flex flex-col h-full" data-testid={`resource-card-${r.id}`}>
            <div className={featured ? "aspect-[16/9]" : "aspect-[16/8]"}>
                <ResourceThumb r={r} />
            </div>
            <div className="flex flex-col flex-1 p-7 lg:p-8">
                <div className="flex items-center justify-between gap-3">
                    <span className="res-eyebrow" data-testid={`resource-category-${r.id}`}>{categoryName || "Resource"}</span>
                    <span
                        className="inline-flex items-center gap-1.5 text-[13px] px-2.5 py-1 rounded-full"
                        style={{ background: meta.tint, color: "#4A4A30" }}
                        data-testid={`resource-type-${r.id}`}
                    >
                        <meta.Icon size={14} strokeWidth={1.6} aria-hidden /> {meta.label}
                    </span>
                </div>
                <h3 className="mt-4 font-serif text-[28px] leading-[1.15]" style={{ color: "#4A4A30" }} data-testid={`resource-title-${r.id}`}>
                    <Link to={`/resources/${r.slug}`} className="hover:underline decoration-[#CFA969] underline-offset-4" data-testid={`resource-title-link-${r.id}`}>{r.title}</Link>
                </h3>
                {r.description && (
                    <p className="mt-3 text-[16px] leading-[1.7]" style={{ color: "#5C5C47" }}>{r.description}</p>
                )}
                <div className="mt-auto pt-7">
                    <CardAction r={r} onWatch={onWatch} />
                </div>
            </div>
        </article>
    );
};
